import React, { Fragment, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";
import { Container, Modal } from "react-bootstrap";
import { FaCalendarAlt } from "react-icons/fa";
import JajotApi from "../../api/JajotApi";
import TaskWizard from "../task/TaskWizard";
import "./CalendarDashboard.css";
import Heading from "../common/Heading";

const CalendarDashboard = () => {
  const location = useLocation();
  const today = moment();
  const minDate = today.clone().subtract(1, "month");
  const maxDate = today.clone().add(3, "months");

  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [events, setEvents] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleDateClick = (arg) => {
    setSelectedDate(arg.dateStr);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingRow(null);
  };

  const handleEditClick = (rows) => {
    const clickedTaskId = rows?.event?._def?.publicId;
    if (!clickedTaskId) return;

    const selectedEvent = events.find(event => event.id === clickedTaskId);
    if (selectedEvent?.taskData) {
      setEditingRow(selectedEvent.taskData);
      setShowModal(true);
    }
  };

  const handleEventCreated = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await JajotApi.getTasksRecords(null, 'Event');
        const apiEvents = response.success && response.records
          ? response.records.map((task) => ({
            id: task.Id,
            title: task.Title__c || task.Name || "Untitled",
            start: task.Start_Date__c ? new Date(task.Start_Date__c) : null,
            end: task.End_Date__c ? new Date(task.End_Date__c) : null,
            status: task.Status__c || "New",
            description: task.Description__c || "",
            assignees: task.Task_Assignees__r?.records || [],
            // Store complete task data for editing
            taskData: {
              Id: task.Id,
              Title: task.Title__c,
              Status: task.Status__c,
              OwnerId: task.OwnerId__c,
              StartDate: task.Start_Date__c,
              EndDate: task.End_Date__c,
              Priority: task.Priority__c,
              Description: task.Description__c,
              Assignees: task.Task_Assignees__r?.records?.map(assignee => ({
                recordId: assignee.Id,
                assigneeId: assignee.ContactId__c,
                assigneeName: assignee.ContactId__r?.Full_Name__c || assignee.ContactId__c
              })) || []
            }
          }))
          : [];
        setEvents(apiEvents);
      } catch (error) {
        console.error("Error fetching tasks:", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [refresh]);

  const customEventContent = (eventInfo) => {
    const statusColors = {
      "New": "#3498db",
      "In Progress": "#f39c12",
      "Completed": "#27ae60",
      "Cancelled": "#e74c3c"
    };
    const status = eventInfo.event.extendedProps.status || "New";
    const bgColor = "#40826D";

    const startTime = eventInfo.event.start
      ? moment(eventInfo.event.start).format("HH:mm")
      : "";
    const endTime = eventInfo.event.end
      ? moment(eventInfo.event.end).format("HH:mm")
      : "";

    return (
      <div
        className="px-2 py-1 rounded text-white fw-semibold h-100 d-flex flex-column justify-content-between"
        style={{
          backgroundColor: bgColor,
          fontSize: "11px",
          overflow: "hidden",
          border: "none",
          opacity: 1,
          filter: "none",
          minHeight: "100%"
        }}
      >
        <div style={{
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          fontWeight: "600"
        }}>
          {eventInfo.event.title}
        </div>
        {(startTime || endTime) && (
          <div style={{
            fontSize: "9px",
            opacity: 0.95,
            marginTop: "2px",
            whiteSpace: "nowrap"
          }}>
            {startTime && endTime ? `${startTime} - ${endTime}` : startTime || endTime}
          </div>
        )}
      </div>
    );
  };

  return (
    <Fragment>
      <div style={{ position: "relative" }}>
        <Container fluid className="px-3 px-md-4 py-4">
          <Heading headingName="Event Calendar" />

          <div className="bg-white p-4 rounded shadow-sm border mt-4">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              events={events}
              displayEventEnd={true}
              themeSystem="bootstrap5"
              height="auto"
              validRange={{
                start: minDate.format("YYYY-MM-DD"),
                end: maxDate.format("YYYY-MM-DD"),
              }}
              eventContent={customEventContent}
              dateClick={handleDateClick}
              eventClick={handleEditClick}
              eventClassNames="shadow-sm"
              buttonText={{
                today: "Today",
                month: "Month",
                week: "Week",
                day: "Day"
              }}
            />
          </div>
        </Container>

        {/* Task Modal for Create/Edit */}
        <Modal size="lg" show={showModal} onHide={handleClose} centered scrollable backdrop="static">
          <Modal.Header closeButton style={{ backgroundColor: "var(--bs-primary)", color: "white" }}>
            <Modal.Title className="d-flex align-items-center gap-2">
              <FaCalendarAlt size={20} />
              <span>{editingRow?.Id ? "Edit Task" : "Create New Task"}</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            <TaskWizard
              show={showModal}
              hideHeader={false}
              taskToEdit={editingRow}
              selectedDate={selectedDate}
              onTaskCreated={() => {
                handleClose();
                handleEventCreated();
              }}
            />
          </Modal.Body>
        </Modal>


      </div>
    </Fragment>
  );
};

export default CalendarDashboard;
